import React from 'react';
import { dehydrate, QueryClient } from '@tanstack/react-query';
import { Skeleton } from 'antd';
import { LocationIcon } from 'icons';
import { GetStaticProps } from 'next';
import { useTranslations } from 'next-intl';
import { StringParam, useQueryParams, withDefault } from 'use-query-params';

import { UniversitiesCard } from 'modules/blok-test';
import { getRegions, globalConst, isEmptyArr } from 'modules/global';
import { getAllUniversities, universitiesConst, UniversityTypes, useAllUniversities } from 'modules/universities';

import { CustomPagination, EmptyData, NextHead, SearchInput, Visible } from 'components';

const Index = () => {
  const t = useTranslations();
  const [query] = useQueryParams({
    search: withDefault(StringParam, ''),
    page: withDefault(StringParam, '1'),
    address_id: withDefault(StringParam, '')
  });

  const { universities, isLoading, refetch } = useAllUniversities({
    search: query?.search || undefined,
    addressId: query.address_id === '99' ? undefined : query.address_id,
    type: UniversityTypes.PRIVATE,
    page: query.search ? undefined : query.page
  });

  return (
    <>
      <NextHead title={t('meta.private_universities.title')} description={t('meta.private_universities.description')} />

      <div className="mx-auto min-h-screen max-w-screen p-4 md:my-7">
        <h1 className="text-xl font-bold md:text-2xl">{t('privateUniverTitle')}</h1>

        <div>
          <div className="mb-5 mt-2 flex flex-col gap-3 rounded-xl bg-transparent sm:flex-row sm:items-center sm:justify-between sm:bg-white sm:px-4 sm:py-2 md:mt-8 md:py-3 md:shadow-sm">
            <div className="hidden items-center gap-2 text-lg font-medium md:flex">
              <span className="text-green-500">
                <LocationIcon />
              </span>
              {t('home.oliygohlar.button_title')}
            </div>
            <SearchInput refetch={refetch} loading={isLoading} />
          </div>
          <Visible
            if={isLoading}
            else={
              <Visible
                if={isEmptyArr(universities?.data)}
                else={
                  <div className="mt-3 grid grid-cols-1 gap-4 self-start sm:col-span-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                    {universities?.data?.map((uni, index) => <UniversitiesCard index={index} key={uni.id} data={uni} />)}
                  </div>
                }
              >
                <EmptyData title={t('malumot_topilmadi')} />
              </Visible>
            }
          >
            <div className="mt-3 grid grid-cols-1 gap-4 self-start sm:col-span-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
              {Array.from({ length: 10 }).map((_, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <Skeleton.Button key={index} active className="!h-45 !w-full !rounded-xl" />
              ))}
            </div>
          </Visible>
          {universities?.data && (
            <div className="mt-5">
              <CustomPagination pageSize={12} total={universities.pagination?.total_items} />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Index;

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  const queryClient = new QueryClient();

  await Promise.allSettled([
    queryClient.prefetchQuery({
      queryKey: [
        universitiesConst.queryKeys.ALL_UNIVERSITIES,
        { addressId: '', page: '1', type: UniversityTypes.PRIVATE, size: 12 }
      ],
      queryFn: getAllUniversities({ type: UniversityTypes.PRIVATE })
    }),
    queryClient.prefetchQuery({
      queryKey: [globalConst.queryKeys.GET_ALL_REGION],
      queryFn: getRegions
    })
  ]);

  return {
    props: {
      dehydratedState: dehydrate(queryClient),
      messages: (await import(`../../messages/${locale}.json`)).default
    },
    revalidate: 604800
  };
};
